import React, { useContext, useState } from "react";
import "./App.css";
import { Route, Routes } from "react-router";
import Main from "./pages/Main";
import Login from "./pages/Login";
import DashboardIcon from "@mui/icons-material/Dashboard";
import Sidebar from "./components/SideBar";
import Robots from "./pages/Robots";
import AccountPage from "./pages/AccountPage";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { darkTheme } from "./theme/theme";
import { PageContext, setCreds } from "./contexts/PageContext";
import {
  Group,
  WebAsset,
  PrecisionManufacturing,
  ModelTrainingTwoTone,
  Public,
  GamepadOutlined,
} from "@mui/icons-material";
import { User, UserRole } from "./models/User";
import OrgsPage from "./pages/OrgsPage";
import EditOrgPage from "./pages/EditOrgPage";
import UsersPage from "./pages/UsersPage";
import EditUserPage from "./pages/EditUserPage";
import MapPage from "./pages/MapPage";
import UniversalDialog, {
  initProgressSetter,
  initSnackbar,
  initUniversalDialog,
  SnackbarComponent,
  UniversalDialogProps,
  UniversalDialogType,
} from "./components/UniversalDialog";
import EditRobotPage from "./pages/EditRobotPage";
import ControlPanelPage from "./pages/ControlPanelPage";
import AssetsPage from "./pages/AssetsPage";
import EditSitePage from "./pages/EditSitePage";
import { APIProvider } from "@vis.gl/react-google-maps";

import { DeploymentsPage } from "./pages/deployment/DeploymentsPage";
import { DeploymentPage } from "./pages/deployment/DeploymentPage";
import { EditDeploymentPage } from "./pages/deployment/EditDeploymentPage";
import Maintenance404 from "./pages/Maintenance404";
import { EditAssetDescriptionPage } from "./pages/EditAssetDescriptionPage";
import { SidebarProvider, useSidebarContext } from "./contexts/SideBarContext";
import { SitesView } from "./views/SitesView";
import { ModelsView } from "./views/ModelsView";
import { Box } from "lucide-react";
import { runningOnRobot } from "./api/host-aware";
import { RobotLandingPage } from "./pages/RobotLandingPage";
import { Remotes } from "./pages/Remotes";
import { EditRemotePage } from "./pages/EditRemotePage";
import { HMISupportPage } from "./pages/HMISupportPage";

declare module "@mui/material/styles" {
  interface PaletteOptions {
    custom?: {
      card?: string;
      buttonBorder?: string;
    };
  }
}

export interface NavItem {
  text: string;
  icon: React.ReactNode;
  href: string;
}


const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;

function App() {
  const { isLoggedIn, user } = useContext(PageContext);
  const [universalDialog, setUniversalDialog] = useState<UniversalDialogProps>({
    title: "",
    text: "",
    openDialog: false,
    type: UniversalDialogType.Alert,
    onComplete: (yes: boolean, inputValue: string) => { },
    progress: 0, // Remove setProgress from initial state
  });
  const recentSceneId = user?.preferences?.recentSceneId || "";
  const [progress, setProgress] = useState(0);

  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("info");

  const handleSnackClose = () => {
    setSnackMessage("");
  };


  initSnackbar(setSnackMessage, setSnackSeverity);
  initUniversalDialog(setUniversalDialog);
  initProgressSetter(setProgress);

  const isAdmin = user?.role === UserRole.SystemAdmin || user?.role === UserRole.OrgAdmin;
  const isSystemAdmin = user?.role === UserRole.SystemAdmin;

  const navItems: NavItem[] = [
    { text: "Dashboard", icon: <DashboardIcon />, href: "/dashboard" },
    { text: "Deployments", icon: <WebAsset />, href: "/deployments" },
    { text: "World", icon: <Public />, href: "/sites/all" },
    { text: "Robot Ops", icon: <PrecisionManufacturing />, href: "/robots" },
    { text: "Remotes", icon: <GamepadOutlined />, href: "/remotes" },
    { text: "Models", icon: <Box />, href: "/models/new" },
    ...(isAdmin || isSystemAdmin
      ? [{ text: "Organizations", icon: <Group />, href: isSystemAdmin ? "/orgs" : `/edit-org/${user.orgId}` }]
      : []),
  ];

  if (runningOnRobot()) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <RobotLandingPage />
      </ThemeProvider>
    );
  } else if (window.location.pathname === '/hmi') {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <HMISupportPage />
      </ThemeProvider>
    );
  } else {
    return (
      <APIProvider apiKey={googleMapsApiKey} libraries={["geometry"]}>
        <SidebarProvider>
          <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <AppContent
              isLoggedIn={isLoggedIn}
              user={user ?? null}
              navItems={navItems}
              universalDialog={universalDialog}
              progress={progress}
              snackMessage={snackMessage}
              snackSeverity={snackSeverity}
              handleSnackClose={handleSnackClose}
            />
          </ThemeProvider>
        </SidebarProvider>
      </APIProvider>
    );
  }
}

interface AppContentProps {
  isLoggedIn: boolean;
  user: User | null;
  navItems: NavItem[];
  universalDialog: UniversalDialogProps;
  progress: number;
  snackMessage: string;
  snackSeverity: "success" | "error" | "warning" | "info";
  handleSnackClose: () => void;
}

function AppContent({
  isLoggedIn,
  user,
  navItems,
  universalDialog,
  progress,
  snackMessage,
  snackSeverity,
  handleSnackClose,
}: AppContentProps) {
  const { isCollapsed } = useSidebarContext();
  const isAdmin = user?.role === UserRole.SystemAdmin || user?.role === UserRole.OrgAdmin;
  const isSystemAdmin = user?.role === UserRole.SystemAdmin;
  const orgId = user?.orgId || "";

  // const nonAlphaVersionLoaded = Boolean(
  //   globalThis &&
  //     globalThis.google?.maps?.version &&
  // );

  // if (nonAlphaVersionLoaded) {
  //   location.reload();
  //   return;
  // }

  return (
    <>
      {isLoggedIn && user && (
        <Sidebar
          navItems={navItems}
          drawerWidthCollapsed={72}
          drawerWidthOpen={240}
        />
      )}
      <div
        style={{
          marginLeft: isCollapsed ? 72 : 240,
          transition: "margin-left 0.3s ease",
        }}
      >
        {!user ? (
          <Login />
        ) : (
          <Routes>
            {runningOnRobot() ? <Route path="/" element={<RobotLandingPage />} /> : <Route path="/" element={<Login />} />}
            <Route path='/rlp' element={<RobotLandingPage />} />
            <Route path='/hmi' element={<HMISupportPage />} />
            <Route path="/remotes" element={<Remotes />} />
            <Route path="/edit-remote/:id" element={<EditRemotePage />} />
            <Route path="/dashboard" element={<Main />} />
            <Route path="/deployments" element={<DeploymentsPage />} />
            <Route path="/deployment/:id" element={<DeploymentPage />} />
            <Route path="/robots" element={<Robots />} />
            {/* <Route path="/sites" element={<SitePage />} /> */}
            <Route path="/sites/:siteId" element={<SitesView />} />
            <Route path="/models/:modelId" element={<ModelsView />} />
            <Route
              path="/map"
              element={<MapPage />}
            />
            <Route path="/my-account" element={<AccountPage />} />
            <Route path="/edit-robot/:id" element={<EditRobotPage />} />
            <Route
              path="/edit-deployment/:id"
              element={<EditDeploymentPage />}
            />
            <Route path="/control-panel/:id" element={<ControlPanelPage />} />
            <Route path="/assets/" element={<AssetsPage />} />
            <Route path="/edit-asset-description/:id" element={<EditAssetDescriptionPage />} />
            <Route path="/edit-site/:id" element={<EditSitePage />} />
            <Route path="/maintenance/404" element={<Maintenance404 />} />
            {isLoggedIn && user && (isAdmin || isSystemAdmin) && (
              <>
                <Route path="/orgs" element={<OrgsPage />} />
                <Route path="/edit-org/:id" element={<EditOrgPage />} />
                <Route path="/users/:orgId" element={<UsersPage />} />
                <Route
                  path="/edit-user/:orgId/:userId"
                  element={<EditUserPage />}
                />
              </>
            )}
          </Routes>
        )}
      </div>
      <SnackbarComponent
        message={snackMessage}
        severity={snackSeverity}
        openSnack={Boolean(snackMessage)}
        autoHideDuration={6000}
        vertical="top"
        horizontal="center"
        onClose={handleSnackClose}
      />
      {Boolean(universalDialog) && <UniversalDialog {...universalDialog} progress={progress} />}
    </>
  );
}

export default App;